import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { articlesAPI, categoriesAPI } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Toast from '../../components/common/Toast';
import MediaPicker from '../../components/MediaPicker';
import ReactQuill from 'react-quill-new';
import 'react-quill/dist/quill.snow.css';

const ArticleForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = !!id;
    const quillRef = useRef(null);

    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(isEdit);
    const [submitting, setSubmitting] = useState(false);
    const [showMediaPicker, setShowMediaPicker] = useState(false);
    const [insertImageMode, setInsertImageMode] = useState(false);
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
    
    const [formData, setFormData] = useState({
        title: '',
        category: '',
        author: '',
        excerpt: '',
        content: '',
        image_url: '',
        video_url: '',
        tags: [],
        read_time: 5,
        is_featured: false,
        is_published: true,
    });

    useEffect(() => {
        fetchCategories();
        if (isEdit) fetchArticle();
    }, [id]);

    const fetchCategories = async () => {
        try {
            const response = await categoriesAPI.getGuidanceCategories();
            if (response.data.success) {
                setCategories(response.data.data.categories);
            }
        } catch (error) {
            console.error('Failed to fetch categories:', error);
        }
    };

    const fetchArticle = async () => {
        try {
            const response = await articlesAPI.getById(id);
            if (response.data.success && response.data.data) {
                const article = response.data.data;
                setFormData({
                    title: article.title || '',
                    category: article.category || '',
                    author: article.author || '',
                    excerpt: article.excerpt || '',
                    content: article.content || '',
                    image_url: article.image_url || '',
                    video_url: article.video_url || '',
                    tags: Array.isArray(article.tags) ? article.tags : [],
                    read_time: article.read_time || 5,
                    is_featured: article.is_featured ?? false,
                    is_published: article.is_published ?? true,
                });
            }
        } catch (error) {
            console.error('Failed to fetch article:', error);
            setToast({ show: true, message: 'Failed to load article', type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);

        try {
            if (isEdit) {
                await articlesAPI.update(id, formData);
                setToast({ show: true, message: 'Article updated successfully', type: 'success' });
            } else {
                await articlesAPI.create(formData);
                setToast({ show: true, message: 'Article created successfully', type: 'success' });
            }
            setTimeout(() => navigate('/articles'), 1500);
        } catch (error) {
            setToast({ 
                show: true, 
                message: error.response?.data?.message || 'Failed to save article', 
                type: 'error' 
            });
        } finally {
            setSubmitting(false);
        }
    };

    // ✅ QUILL TOOLBAR CONFIGURATION
    const modules = {
        toolbar: {
            container: [
                [{ 'header': [1, 2, 3, false] }],
                ['bold', 'italic', 'underline', 'strike'],
                [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                [{ 'indent': '-1'}, { 'indent': '+1' }],
                ['blockquote', 'code-block'],
                [{ 'align': [] }],
                ['link', 'image'],
                [{ 'color': [] }, { 'background': [] }],
                ['clean']
            ],
            handlers: {
                image: imageHandler
            }
        },
        clipboard: {
            matchVisual: false,
        }
    };

    const formats = [
        'header',
        'bold', 'italic', 'underline', 'strike',
        'list', 'bullet', 'indent',
        'blockquote', 'code-block',
        'align',
        'link', 'image',
        'color', 'background'
    ];

    // ✅ IMAGE HANDLER - Opens Media Picker
    function imageHandler() {
        setInsertImageMode(true);
        setShowMediaPicker(true);
    }

    // ✅ HANDLE MEDIA SELECTION
    const handleMediaSelect = (url) => {
        if (insertImageMode) {
            // Insert image into editor
            const quill = quillRef.current.getEditor();
            const range = quill.getSelection();
            quill.insertEmbed(range ? range.index : 0, 'image', url);
            setInsertImageMode(false);
        } else {
            // Set as featured image
            setFormData({ ...formData, image_url: url });
        }
        setShowMediaPicker(false);
    };

    if (loading) {
        return <LoadingSpinner message="Loading article..." />;
    }

    return (
        <div>
            <Toast 
                show={toast.show} 
                message={toast.message} 
                type={toast.type} 
                onClose={() => setToast({ ...toast, show: false })} 
            />

            <div className="mb-4">
                <button 
                    className="btn btn-outline-secondary" 
                    onClick={() => navigate('/articles')}
                >
                    <i className="bi bi-arrow-left me-2"></i>
                    Back to Articles
                </button>
            </div>

            <div className="stat-card">
                <h4 className="mb-4">
                    {isEdit ? 'Edit Article' : 'Create New Article'}
                </h4>

                <form onSubmit={handleSubmit}>
                    <div className="row g-4">
                        {/* Title */}
                        <div className="col-12">
                            <label className="form-label">Title *</label>
                            <input
                                type="text"
                                className="form-control"
                                value={formData.title}
                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                required
                                placeholder="Enter article title"
                            />
                        </div>

                        {/* Category & Author */}
                        <div className="col-md-6">
                            <label className="form-label">Category *</label>
                            <select
                                className="form-select"
                                value={formData.category}
                                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                                required
                            >
                                <option value="">Select category</option>
                                {categories.map((cat) => (
                                    <option key={cat.id} value={cat.name}>{cat.name}</option>
                                ))}
                            </select>
                        </div>

                        <div className="col-md-6">
                            <label className="form-label">Author</label>
                            <input
                                type="text"
                                className="form-control"
                                value={formData.author}
                                onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                                placeholder="Author name"
                            />
                        </div>

                        {/* Excerpt */}
                        <div className="col-12">
                            <label className="form-label">Excerpt *</label>
                            <textarea
                                className="form-control"
                                rows="3"
                                value={formData.excerpt}
                                onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                                required
                                placeholder="Brief summary of the article (appears in article listings)"
                            ></textarea>
                            <small className="text-muted">
                                Aim for 120-160 characters for best display
                            </small>
                        </div>

                        {/* ✅ REACT QUILL EDITOR - REPLACES TEXTAREA */}
                        <div className="col-12">
                            <label className="form-label">Content *</label>
                            <div className="quill-editor-container">
                                <ReactQuill
                                    ref={quillRef}
                                    theme="snow"
                                    value={formData.content}
                                    onChange={(content) => setFormData({ ...formData, content })}
                                    modules={modules}
                                    formats={formats}
                                    placeholder="Write your article content here... Use the toolbar to format text, add images, links, and more."
                                />
                            </div>
                            <div className="character-count">
                                {formData.content.replace(/<[^>]*>/g, '').length} characters
                            </div>
                        </div>

                        {/* Featured Image */}
                        <div className="col-md-6">
                            <label className="form-label">Featured Image</label>
                            <div>
                                {formData.image_url && (
                                    <div className="mb-2 position-relative" style={{ maxWidth: '250px' }}>
                                        <img
                                            src={formData.image_url}
                                            alt="Preview"
                                            className="w-100 rounded"
                                            style={{ height: '180px', objectFit: 'cover' }}
                                        />
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-danger position-absolute top-0 end-0 m-1"
                                            onClick={() => setFormData({ ...formData, image_url: '' })}
                                        >
                                            <i className="bi bi-x-lg"></i>
                                        </button>
                                    </div>
                                )}
                                <button
                                    type="button"
                                    className="btn btn-outline-primary btn-sm"
                                    onClick={() => {
                                        setInsertImageMode(false);
                                        setShowMediaPicker(true);
                                    }}
                                >
                                    <i className="bi bi-image me-2"></i>
                                    {formData.image_url ? 'Change Image' : 'Select Image'}
                                </button>
                            </div>
                        </div>

                        {/* Video URL */}
                        <div className="col-md-6">
                            <label className="form-label">Video URL (Optional)</label>
                            <input
                                type="url"
                                className="form-control"
                                value={formData.video_url}
                                onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                                placeholder="https://youtube.com/watch?v=..."
                            />
                            <small className="text-muted">YouTube or Vimeo URL</small>
                        </div>

                        {/* Tags */}
                        <div className="col-md-6">
                            <label className="form-label">Tags (Optional)</label>
                            <input
                                type="text"
                                className="form-control"
                                value={formData.tags.join(', ')}
                                onChange={(e) => setFormData({ 
                                    ...formData, 
                                    tags: e.target.value.split(',').map(tag => tag.trim()).filter(Boolean) 
                                })}
                                placeholder="wellness, spiritual health, ramadan"
                            />
                            <small className="text-muted">Separate tags with commas</small>
                        </div>

                        {/* Read Time */}
                        <div className="col-md-6">
                            <label className="form-label">Read Time (minutes)</label>
                            <input
                                type="number"
                                className="form-control"
                                value={formData.read_time}
                                onChange={(e) => setFormData({ ...formData, read_time: parseInt(e.target.value) })}
                                min="1"
                                max="60"
                            />
                        </div>

                        {/* Checkboxes */}
                        <div className="col-12">
                            <div className="form-check mb-2">
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    id="is_featured"
                                    checked={formData.is_featured}
                                    onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                                />
                                <label className="form-check-label" htmlFor="is_featured">
                                    <strong>Featured Article</strong>
                                    <small className="d-block text-muted">
                                        Featured articles appear prominently on the home screen
                                    </small>
                                </label>
                            </div>
                            <div className="form-check">
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    id="is_published"
                                    checked={formData.is_published}
                                    onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
                                />
                                <label className="form-check-label" htmlFor="is_published">
                                    <strong>Publish immediately</strong>
                                    <small className="d-block text-muted">
                                        Uncheck to save as draft
                                    </small>
                                </label>
                            </div>
                        </div>

                        {/* Submit Buttons */}
                        <div className="col-12">
                            <div className="d-flex gap-2">
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={submitting}
                                >
                                    {submitting ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2"></span>
                                            Saving...
                                        </>
                                    ) : (
                                        <>
                                            <i className="bi bi-save me-2"></i>
                                            {isEdit ? 'Update Article' : 'Create Article'}
                                        </>
                                    )}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    onClick={() => navigate('/articles')}
                                    disabled={submitting}
                                >
                                    Cancel
                                </button>
                                {!isEdit && (
                                    <button
                                        type="button"
                                        className="btn btn-outline-info ms-auto"
                                        onClick={() => {
                                            setFormData({ ...formData, is_published: false });
                                            setTimeout(() => document.querySelector('form').requestSubmit(), 100);
                                        }}
                                        disabled={submitting}
                                    >
                                        <i className="bi bi-file-earmark me-2"></i>
                                        Save as Draft
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </form>
            </div>

            {/* ✅ MEDIA PICKER MODAL */}
            <MediaPicker
                show={showMediaPicker}
                onClose={() => {
                    setShowMediaPicker(false);
                    setInsertImageMode(false);
                }}
                onSelect={handleMediaSelect}
            />
        </div>
    );
};

export default ArticleForm;